import { Box, Typography, useTheme } from "@mui/material";
import PropTypes from "prop-types";
import defaultTheme from "themeProvider";

const WHEELS = [20, 42, 82];
const LUGS = [0, 72, 144, 216, 288].map((deg) => {
  const r = ((deg - 90) * Math.PI) / 180;
  return [+(Math.cos(r) * 2.6).toFixed(2), +(Math.sin(r) * 2.6).toFixed(2)];
});

const TruckLoader = ({ isSubmitting = false, isSuccessAnim = false }) => {
  const muiTheme = useTheme();
  const palette = (muiTheme?.palette ? muiTheme : defaultTheme).palette;
  const isActive = isSubmitting || isSuccessAnim;

  const status = isSuccessAnim
    ? { text: "Dispatched Successfully!", color: palette.success.dark, fw: 700 }
    : isSubmitting
    ? { text: "Loading into truck...", color: palette.warning.dark, fw: 600 }
    : { text: "Truck ready for load", color: palette.grey[1000], fw: 500 };

  const wheelAnim = isSuccessAnim ? "tlSpin 0.4s linear infinite" : isSubmitting ? "tlSpin 0.8s linear infinite" : "none";
  const roadAnim = isSuccessAnim ? "tlRoad 0.27s linear infinite" : isSubmitting ? "tlRoad 0.54s linear infinite" : "none";
  const skyAnim = isSuccessAnim ? "tlSky 4s linear infinite" : isSubmitting ? "tlSky 8s linear infinite" : "none";

  const cargoItems = [
    { id: "a", x: 20, w: 11, h: 10, delay: 0, rot: "-8deg", color: palette.warning.dark, border: palette.warning.darkest },
    { id: "b", x: 33, w: 10, h: 10, delay: 0.5, rot: "10deg", color: palette.primary.light, border: palette.primary.main },
    { id: "c", x: 45, w: 8, h: 8, delay: 1.0, rot: "-5deg", color: palette.info.skyBlue, border: palette.info.blueNavyColor },
  ];

  const skySvg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='20'%3E%3Cpath fill='%23${palette.grey[300].replace("#", "")}' d='M0 20V12H10V6H22V20H30V10H44V20H54V8H62V3H72V20H84V11H98V20H120V13H108V20Z'/%3E%3C/svg%3E")`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", position: "relative", height: 72, overflow: "hidden", flex: 1, mr: 2.5 }}>
      <style>{`
        @keyframes tlSpin { to { transform: rotate(360deg); } }
        @keyframes tlRoad { to { background-position: -32px 0; } }
        @keyframes tlSky { to { background-position: -120px 0; } }
        @keyframes tlIdle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-0.8px); } }
        @keyframes tlRumble { 0%, 100% { transform: translateY(0); } 20% { transform: translateY(-1.2px) rotate(-0.25deg); } 45% { transform: translateY(0.4px); } }
        @keyframes tlDrive { 0%, 12% { left: 12px; opacity: 1; } 82% { left: calc(100% - 110px); opacity: 1; } 100% { left: calc(100% + 50px); opacity: 0; } }
        @keyframes tlSmoke { 0% { transform: scale(0.4); opacity: 0.85; } 100% { transform: translate(-26px, -14px) scale(2); opacity: 0; } }
        @keyframes tlDust { 0% { transform: scale(0.4); opacity: 0.55; } 100% { transform: translate(-28px, -5px) scale(1.5); opacity: 0; } }
        @keyframes tlDrop { 0% { transform: translateY(-12px); opacity: 0; } 40%, 100% { transform: translateY(22px); opacity: 1; } }
      `}</style>

      <Box sx={{ mb: 0.5, zIndex: 2, height: 20 }}>
        <Typography sx={{ fontSize: isActive ? 13 : 12, fontWeight: status.fw, color: status.color, display: "flex", alignItems: "center", gap: 0.5 }}>
          {status.text}
        </Typography>
      </Box>

      <Box sx={{ position: "relative", width: "100%", height: 48 }}>
        <Box sx={{ position: "absolute", inset: "auto 0 8px 0", height: 20, opacity: 0.6, backgroundImage: skySvg, backgroundRepeat: "repeat-x", backgroundSize: "120px 20px", animation: skyAnim }} />
        <Box sx={{ position: "absolute", inset: "auto 0 0 0", height: 8, backgroundColor: palette.grey.A1000, borderTop: `1px solid ${palette.grey[700]}` }} />
        <Box sx={{ position: "absolute", inset: "auto 0 1px 0", height: 1.5, backgroundImage: `repeating-linear-gradient(90deg, ${palette.grey[100]} 0 16px, transparent 16px 32px)`, backgroundSize: "32px 100%", animation: roadAnim }} />

        <Box sx={{ position: "absolute", bottom: 1, left: 12, display: "flex", alignItems: "center", animation: isSuccessAnim ? "tlDrive 2.4s ease-in forwards" : isSubmitting ? "tlRumble 0.45s infinite ease-in-out" : "tlIdle 2.5s infinite ease-in-out" }}>
          <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i} sx={{ position: "absolute", left: 61.9 - (5 + i * 2) / 2, top: 3 - (5 + i * 2) / 2, width: 5 + i * 2, height: 5 + i * 2, borderRadius: "50%", background: `radial-gradient(circle, rgba(203,213,225,0.9) 0%, ${palette.grey.A800} 55%, transparent 100%)`, animation: isActive ? `tlSmoke 1s infinite ease-out ${i * 0.25}s` : `tlSmoke 3s infinite ease-out ${i * 0.75}s` }} />
            ))}
            {isActive && [0, 1, 2].map((i) => (
              <Box key={`d-${i}`} sx={{ position: "absolute", left: 6, bottom: 0, width: 8 + i * 2, height: 8 + i * 2, borderRadius: "50%", backgroundColor: "rgba(203,213,225,0.55)", animation: `tlDust 0.5s infinite ease-out ${i * 0.17}s` }} />
            ))}
          </Box>

          <svg width="110" height="46" viewBox="0 0 110 46" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ overflow: "visible", display: "block" }}>
            <defs>
              <linearGradient id="tlTrailer" x1="0" y1="6" x2="0" y2="32" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={palette.info.blueNavyColor} />
                <stop offset="100%" stopColor={palette.primary.main} />
              </linearGradient>
              <linearGradient id="tlCab" x1="0" y1="14" x2="0" y2="32" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={palette.primary.light} />
                <stop offset="100%" stopColor={palette.primary.main} />
              </linearGradient>
              <linearGradient id="tlGlass" x1="0" y1="17" x2="0" y2="24.5" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={palette.info.lightest} />
                <stop offset="100%" stopColor={palette.info.light} />
              </linearGradient>
              <linearGradient id="tlChrome" x1="60.6" y1="0" x2="63.2" y2="0" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={palette.grey[100]} />
                <stop offset="50%" stopColor={palette.grey[400]} />
                <stop offset="100%" stopColor={palette.grey[700]} />
              </linearGradient>
              <linearGradient id="tlTank" x1="0" y1="33" x2="0" y2="40" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={palette.grey[300]} />
                <stop offset="100%" stopColor={palette.grey[700]} />
              </linearGradient>
              <linearGradient id="tlBeam" x1="99" y1="0" x2="132" y2="0" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={palette.warning.light} stopOpacity="0.75" />
                <stop offset="100%" stopColor={palette.warning.light} stopOpacity="0" />
              </linearGradient>
              <clipPath id="tlRoofClip"><rect x="-10" y="-40" width="140" height="46.2" /></clipPath>
            </defs>

            <ellipse cx="54" cy="43.6" rx="52" ry="2" fill={palette.grey.A800} opacity="0.25" />
            <rect x="4" y="6" width="60" height="26" rx="3" fill="url(#tlTrailer)" stroke={palette.info.blueNavyColor} strokeWidth="1.5" />
            <line x1="8" y1="7.4" x2="60" y2="7.4" stroke={palette.primary.light} strokeWidth="0.8" opacity="0.5" strokeLinecap="round" />
            {[14, 24, 34, 44, 54].map((x) => (
              <line key={x} x1={x} y1="9" x2={x} y2="29" stroke={palette.primary.light} strokeWidth="0.9" opacity="0.35" />
            ))}

            <g id="truck-jsw-logo">
              <rect x="16" y="11" width="36" height="14" rx="2" fill={palette.primary.main} stroke={palette.common.white} strokeWidth="0.8" opacity="0.95" />
              <text x="34" y="21" textAnchor="middle" fill={palette.common.white} fontSize="8.5" fontWeight="900" fontFamily="'Source Sans Pro', sans-serif" letterSpacing="1.2">JSW</text>
              <line x1="22" y1="23.5" x2="46" y2="23.5" stroke={palette.error.dark} strokeWidth="1.2" strokeLinecap="round" />
            </g>

            <line x1="6" y1="29.4" x2="62" y2="29.4" stroke={palette.buttonError.main} strokeWidth="1.4" />
            <line x1="6" y1="29.4" x2="62" y2="29.4" stroke={palette.common.white} strokeWidth="1.4" strokeDasharray="3 3" />
            <line x1="7.5" y1="8.5" x2="7.5" y2="30" stroke={palette.info.blueNavyColor} strokeWidth="0.9" />
            <rect x="8.4" y="17" width="1.2" height="6" rx="0.6" fill={palette.grey[300]} />
            <rect x="4.6" y="24" width="2" height="4" rx="0.8" fill={palette.buttonError.main} />
            <rect x="2" y="32.8" width="6" height="2.4" rx="1" fill={palette.grey.A1000} />

            <rect x="13" y="4.2" width="42" height="1" rx="0.5" fill={palette.grey[400]} />
            <rect x="14" y="5.2" width="40" height="2" fill={palette.grey.A800} />
            <rect x="14" y="4.6" width="40" height="2.6" rx="0.8" fill={palette.grey[800]} stroke={palette.grey.A800} strokeWidth="0.6" style={{ transformBox: "fill-box", transformOrigin: "left center", transform: isSubmitting ? "scaleX(0.06)" : "scaleX(1)", transition: "transform 0.4s ease" }} />

            {isSubmitting && (
              <g clipPath="url(#tlRoofClip)">
                {cargoItems.map((c) => (
                  <g key={c.id} style={{ transformBox: "fill-box", transformOrigin: "center", animation: `tlDrop 1.5s ease-in ${c.delay}s infinite` }}>
                    <rect x={c.x} y={6 - c.h} width={c.w} height={c.h} rx="1" fill={c.color} stroke={c.border} strokeWidth="1" />
                    <rect x={c.x} y={6 - c.h / 2 - 0.9} width={c.w} height="1.8" fill={palette.warning.light} opacity="0.9" />
                  </g>
                ))}
              </g>
            )}

            <rect x="8" y="32" width="86" height="3" rx="1.5" fill={palette.grey.A1000} />
            <rect x="50" y="33" width="16" height="7" rx="3.5" fill="url(#tlTank)" stroke={palette.grey[700]} strokeWidth="0.6" />
            <line x1="55" y1="33" x2="55" y2="40" stroke={palette.grey.A800} strokeWidth="1" />
            <line x1="61" y1="33" x2="61" y2="40" stroke={palette.grey.A800} strokeWidth="1" />
            <rect x="62" y="24" width="12" height="8" fill={palette.grey.A800} />
            <path d="M60 27Q63 21 66 27" stroke={palette.error.dark} strokeWidth="0.9" fill="none" strokeLinecap="round" />
            <path d="M60.8 28Q63.8 22 66.8 28" stroke={palette.info.skyBlue} strokeWidth="0.9" fill="none" strokeLinecap="round" />
            <rect x="60.6" y="5" width="2.6" height="20" rx="1.1" fill="url(#tlChrome)" stroke={palette.grey[700]} strokeWidth="0.4" />
            <path d="M60.2 5.4L63.6 5.4L62.9 3.6H60.9Z" fill={palette.grey[400]} />

            <path d="M66 14H84C86.5 14 88.5 15.5 89.8 17.6L97.2 26.2C98.2 27.4 98.8 28.9 98.8 30.5V32H66V14Z" fill="url(#tlCab)" stroke={palette.info.blueNavyColor} strokeWidth="1.5" />
            <line x1="67.5" y1="15.3" x2="84" y2="15.3" stroke={palette.primary.light} strokeWidth="0.8" opacity="0.6" strokeLinecap="round" />
            <path d="M70.6 16.5V30.6" stroke={palette.info.blueNavyColor} strokeWidth="0.8" />
            <path d="M70.6 30.6H96" stroke={palette.info.blueNavyColor} strokeWidth="0.8" opacity="0.7" />
            <rect x="74" y="26.6" width="4.2" height="1.2" rx="0.6" fill={palette.grey[200]} />
            <path d="M72 17H82.5L88 24.5H72V17Z" fill="url(#tlGlass)" stroke={palette.info.light} strokeWidth="1" />
            <polygon points="75.5,17 79.5,17 75,24.5 72.5,24.5" fill={palette.common.white} opacity="0.3" />
            <rect x="88.6" y="18.6" width="2.4" height="5.2" rx="1" fill={palette.grey.A800} />
            <rect x="93" y="30.6" width="8.5" height="3.2" rx="1.2" fill={palette.grey[200]} stroke={palette.grey[700]} strokeWidth="0.6" />
            <rect x="95" y="29.2" width="1.6" height="1.2" rx="0.4" fill={palette.warning.dark} />
            {isActive && <polygon points="99,28.4 132,22 132,39 99,31.4" fill="url(#tlBeam)" />}
            <rect x="96" y="27.6" width="3" height="3.4" rx="1" fill={isActive ? palette.warning.light : palette.grey[300]} stroke={palette.grey[700]} strokeWidth="0.4" />

            {WHEELS.map((x) => (
              <g key={x}>
                <path d={`M${x - 9.4} 35A9.4 9.4 0 0 1 ${x + 9.4} 35Z`} fill={palette.grey.A800} />
                <g style={{ transformBox: "view-box", transformOrigin: `${x}px 35px`, animation: wheelAnim }}>
                  <circle cx={x} cy={35} r={7.5} fill={palette.grey.A800} />
                  <circle cx={x} cy={35} r={6.9} fill="none" stroke={palette.grey.A1000} strokeWidth={1.2} strokeDasharray="2.4 1.2" />
                  <circle cx={x} cy={35} r={5.2} fill={palette.grey[800]} />
                  <circle cx={x} cy={35} r={4.2} fill={palette.grey[300]} stroke={palette.grey[700]} strokeWidth={0.6} />
                  {LUGS.map(([dx, dy], i) => (
                    <circle key={i} cx={x + dx} cy={35 + dy} r={0.6} fill={palette.grey[1000]} />
                  ))}
                  <circle cx={x} cy={35} r={1.2} fill={palette.primary.main} />
                </g>
              </g>
            ))}
            <rect x="8.4" y="33" width="1.8" height="8" rx="0.6" fill={palette.grey.A800} />
          </svg>
        </Box>
      </Box>
    </Box>
  );
};

TruckLoader.propTypes = {
  isSubmitting: PropTypes.bool,
  isSuccessAnim: PropTypes.bool,
};

export default TruckLoader;
